import CloseIcon from './CloseIcon.jsx';
import { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useModalFocus } from '../hooks/useModalFocus.js';

// VerdictDrilldown, the "why this score" drill-down (mobile IA spec §4).
// Stacked tiers, each exitable with one back tap:
//   Tier 1, ranked factor list, sorted worst→best
//   Tier 2, per-factor detail: hourly series (Clouds/Transparency, from
//            verdict.hourly) or a condition explanation (Moon/Sky/LP/Kp),
//            with a source/confidence footer (the trust signal).
// Cells carry the numeric value as well as the color (redundant encoding,
// spec §5, survives red-mode monochrome and color-blindness).

const MONO = 'var(--mono)';
const VALENCE_COLOR = { good: 'var(--green)', mid: 'var(--cyan)', bad: 'var(--rose)' };
const VALENCE_RANK  = { bad: 0, mid: 1, good: 2 };
const VALENCE_WORD  = { bad: 'working against you', mid: 'mixed', good: 'in your favor' };

// Quality hue for an hourly cell, by metric direction (higher = worse for both
// cloud % and humidity %).
function cellTone(pct) {
  if (pct == null) return 'var(--bg-2)';
  if (pct <= 25) return 'rgba(87,208,138,0.22)';
  if (pct <= 60) return 'rgba(84,196,236,0.18)';
  return 'rgba(224,121,95,0.24)';
}

const EXPLAIN = {
  Moon: (c) => c?.moon_up
    ? `The Moon is ${Math.round((c.moon_phase_fraction ?? 0) * 100)}% illuminated and ${c.moon_alt_deg}° above the horizon, bright moonlight washes out faint targets.`
    : `The Moon is below the horizon, so moonlight is not a factor right now.`,
  Sky: (c) => ({
    daylight:     'The Sun is up, no astronomical observing until it sets.',
    civil:        'Civil twilight, only the brightest objects are visible yet.',
    nautical:     'Nautical twilight, the sky is darkening; bright targets emerge.',
    astronomical: 'Fully dark (astronomical darkness), the best the sky gets tonight.',
  }[c?.darkness] || `Sun altitude ${c?.sun_alt_deg}°.`),
  'Light pollution': (c) => c?.bortle == null
    ? 'Outside light-pollution data coverage (CONUS only), not weighted in the score.'
    : `Bortle class ${c.bortle} (${c.bortle_label}). Lower is darker; class 1–3 skies show the Milky Way.`,
  Kp: (c) => c?.kp == null
    ? 'No current geomagnetic reading.'
    : `Planetary Kp is ${c.kp}. Aurora reaches your latitude only when Kp clears the local threshold.`,
};

function HourlyStrip({ times, values, unit }) {
  if (!times?.length) {
    return (
      <div style={{ color: 'var(--txt-dim)', fontFamily: MONO, fontSize: 'var(--ui-label)', padding: '12px 0' }}>
        Hourly data unavailable.
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', overflowX: 'auto', gap: 2, paddingBottom: 6, WebkitOverflowScrolling: 'touch' }}>
      {times.map((t, i) => {
        const v = values?.[i];
        const hr = new Date(t).toLocaleTimeString([], { hour: 'numeric' });
        return (
          <div key={t} style={{
            minWidth: 48, height: 64, flexShrink: 0,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            gap: 4, borderRadius: 4,
            background: cellTone(v),
            border: '1px solid var(--hair)',
          }}>
            <span style={{ fontFamily: MONO, fontSize: 'var(--ui-body)', fontWeight: 700, color: 'var(--txt-bright)' }}>
              {v == null ? '-' : `${Math.round(v)}${unit}`}
            </span>
            <span style={{ fontFamily: MONO, fontSize: 'var(--ui-small)', color: 'var(--txt-dim)' }}>{hr}</span>
          </div>
        );
      })}
    </div>
  );
}

function FactorDetail({ factor, verdict }) {
  const h = verdict?.hourly;
  const c = verdict?.conditions;
  if (factor.key === 'Clouds') {
    return (
      <>
        <div style={{ fontFamily: MONO, fontSize: 'var(--ui-label)', color: 'var(--txt-dim)', margin: '8px 0' }}>
          CLOUD COVER · NEXT 12 HOURS
        </div>
        <HourlyStrip times={h?.time} values={h?.cloud_pct} unit="%" />
        {c?.cloud_min_pct != null && (
          <div style={{ fontSize: 'var(--ui-label)', color: 'var(--txt)', marginTop: 8 }}>
            Best window tonight: {c.cloud_min_pct}% cloud.
          </div>
        )}
      </>
    );
  }
  if (factor.key === 'Transparency') {
    return (
      <>
        <div style={{ fontFamily: MONO, fontSize: 'var(--ui-label)', color: 'var(--txt-dim)', margin: '8px 0' }}>
          HUMIDITY · NEXT 12 HOURS (high humidity = hazy skies)
        </div>
        <HourlyStrip times={h?.time} values={h?.humidity} unit="%" />
        <div style={{ fontFamily: MONO, fontSize: 'var(--ui-label)', color: 'var(--txt-dim)', margin: '12px 0 8px' }}>
          HIGH CLOUD · NEXT 12 HOURS
        </div>
        <HourlyStrip times={h?.time} values={h?.cloud_high} unit="%" />
      </>
    );
  }
  const explain = EXPLAIN[factor.key];
  return (
    <div style={{ fontSize: 'var(--ui-body)', lineHeight: 1.6, color: 'var(--txt)', padding: '10px 0' }}>
      {explain ? explain(c) : `Current reading: ${factor.value}.`}
    </div>
  );
}

function sourceFor(key, verdict) {
  const s = verdict?.sources || {};
  if (key === 'Clouds' || key === 'Transparency') return s.weather;
  if (key === 'Light pollution') return s.light_pollution;
  if (key === 'Kp') return s.space_weather;
  return s.ephemeris;
}

export default function VerdictDrilldown({ verdict, initialFactor = null, onClose }) {
  const [factor, setFactor] = useState(initialFactor); // null = Tier 1 list
  const dialog = useRef(null);
  useModalFocus(dialog, onClose, !!verdict);
  if (!verdict) return null;
  const ranked = [...(verdict.factors || [])]
    .sort((a, b) => (VALENCE_RANK[a.valence] ?? 1) - (VALENCE_RANK[b.valence] ?? 1));

  return createPortal(
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 9998,
        background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(2px)',
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        ref={dialog} tabIndex={-1} aria-modal="true" role="dialog" aria-label={factor ? `${factor.key} detail` : 'Score breakdown'}
        style={{
          width: '100%', maxHeight: '80vh', overflowY: 'auto',
          background: 'var(--bg-2)', borderTop: '1px solid var(--hair-2)',
          borderRadius: '10px 10px 0 0', padding: '0 16px 16px',
        }}
      >
        {/* header, back pops exactly one tier */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8, position: 'sticky', top: 0,
          background: 'var(--bg-2)', padding: '14px 0 10px', zIndex: 1,
        }}>
          <button title={factor ? 'Back to score breakdown' : 'Close'} className="og-close-control"
            onClick={() => (factor ? setFactor(null) : onClose())}
            aria-label={factor ? 'Back to score breakdown' : 'Close'}
            style={{
              width: 44, height: 44, background: 'var(--bg-2)', border: '1px solid var(--hair-2)',
              borderRadius: 4, color: 'var(--accent)', fontSize: 15, cursor: 'pointer',
            }}
          >{factor ? '‹' : <CloseIcon />}</button>
          {factor && <button title="Close" className="og-close-control" onClick={onClose} aria-label="Close" style={{minHeight:44,minWidth:44}}><CloseIcon /></button>}
          <h2 style={{
            fontFamily: MONO, fontSize: 'var(--ui-body)', fontWeight: 700, letterSpacing: '0.1em',
            color: 'var(--txt-bright)', textTransform: 'uppercase', margin: 0,
          }}>
            {factor ? factor.key : `Why ${verdict.score} · ${verdict.verdict}`}
          </h2>
        </div>

        {!factor && ranked.map((f, i) => (
          <button
            key={f.key}
            onClick={() => setFactor(f)}
            style={{
              display: 'flex', alignItems: 'center', gap: 10, width: '100%', minHeight: 48,
              padding: '10px 12px', marginBottom: 6, textAlign: 'left',
              background: 'var(--bg-2)', border: '1px solid var(--hair)', borderRadius: 4,
              cursor: 'pointer', color: 'var(--txt)',
            }}
          >
            {/* non-color valence cue: rank number + word, not just the hue */}
            <span style={{ fontFamily: MONO, fontSize: 'var(--ui-label)', color: 'var(--txt-dim)', width: 16 }}>{i + 1}</span>
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ display: 'block', fontFamily: MONO, fontSize: 'var(--ui-body)', fontWeight: 700 }}>{f.key}</span>
              <span style={{ display: 'block', fontSize: 'var(--ui-label)', color: VALENCE_COLOR[f.valence] || 'var(--txt-dim)' }}>
                {f.value} · {VALENCE_WORD[f.valence] || f.valence}
              </span>
            </span>
            <span aria-hidden="true" style={{ color: 'var(--txt-dim)' }}>›</span>
          </button>
        ))}

        {factor && (
          <>
            <FactorDetail factor={factor} verdict={verdict} />
            <div style={{
              marginTop: 14, paddingTop: 10, borderTop: '1px solid var(--hair)',
              fontFamily: MONO, fontSize: 'var(--ui-label)', color: 'var(--txt-dim)',
            }}>
              Source: {sourceFor(factor.key, verdict) || 'OrbitalGlass verdict engine'}
            </div>
          </>
        )}
      </div>
    </div>, document.body
  );
}
